import { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { IconProp, library } from "@fortawesome/fontawesome-svg-core";
import { faFile, faFileExcel, faFilePdf, faFileWord } from "@fortawesome/free-regular-svg-icons";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { hasValue, styles } from "@repo/helpers";

import { Button } from "../button";
import { ButtonSize } from "../button/types";
import inputStyle from "../input/style.module.scss";
import style from "./style.module.scss";
import { MarkupProps, SerializedFile } from "./types";

library.add(faTimes, faFileExcel as any, faFilePdf as any, faFileWord as any, faFile as any);

export default function FileUploadMarkup(props: MarkupProps) {
	const {
		id,
		label,
		disabled,
		focused,
		multiple,
		acceptFileTypes,
		fileInputRef,
		files,
		showFileIcons,
		autoReplace,
		errorText,
		uploadedFiles,
	} = props;
	const {
		onDragOver,
		onDragLeave,
		onDrop,
		addFiles,
		removeFile,
		openFileDialog,
		getFileFromServer,
		removeUploadedFile,
	} = props;

	const deleteUploadedFile = useCallback(
		(file: SerializedFile) => removeUploadedFile(file.id, id + "Uploaded"),
		[removeUploadedFile],
	);

	const getUploadedFile = useCallback(
		(file: SerializedFile) => getFileFromServer && getFileFromServer(file.id),
		[getFileFromServer],
	);

	return (
		<div className={inputStyle.formGroup}>
			<label htmlFor={id} className={inputStyle.controlLabel}>
				{label}
			</label>
			<div
				className={styles(style.fileUploadContainer, focused && style.hover, disabled && style.disabled)}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={onDrop}
			>
				<input
					type="file"
					className={style.fileInput}
					onChange={addFiles}
					multiple={multiple}
					ref={fileInputRef}
					accept={acceptFileTypes ? acceptFileTypes.join(",") : null}
				/>
				<FileList files={files} showIcons={showFileIcons} removeFile={removeFile} />
				<div className={style.fileUploader}>
					<Button
						className={!multiple && !autoReplace && files.length > 0 ? style.uploadButtonHidden : null}
						onClick={openFileDialog}
						disabled={disabled}
						size={ButtonSize.medium}
					>
						Upload File{multiple && "s"}
					</Button>
				</div>
			</div>
			{renderUploadedFiles()}
			<p className={inputStyle.error}>{errorText}</p>
		</div>
	);

	function renderUploadedFiles() {
		if (!uploadedFiles) return null;
		if (!uploadedFiles.filter(hasValue).length) return null;
		return <div className={style.uploadedFilesContainer}>{uploadedFiles.map(renderUploadedFile)}</div>;
	}

	function renderUploadedFile(file: SerializedFile) {
		function FileDeleteButton() {
			if (disabled || !removeUploadedFile) return null;
			return (
				<span
					className={style.deleteButton}
					title={`Delete ${file.name}`}
					onClick={() => deleteUploadedFile(file)}
				>
					<FontAwesomeIcon icon={faTimes} />
				</span>
			);
		}

		return (
			<div key={file.id} className={style.uploadedFile}>
				<FileDeleteButton />
				<div
					className={styles(style.uploadedFileName, file.deleted && style.deleted)}
					title={`Download ${file.name}`}
					onClick={() => getUploadedFile(file)}
				>
					{file.name}
				</div>
			</div>
		);
	}
}

const FileList: FC<FileListProps> = ({ files, showIcons, removeFile }) => {
	const [fileKey, setFileKey] = useState(0);
	const fileCount = useMemo(() => files?.length ?? 0, [files]);
	useEffect(() => {
		setFileKey(fileKey + fileCount + 1);
	}, [fileCount]);
	return <div className={style.filesContainer}>{files.map(renderFileItem)}</div>;
	function renderFileItem(file: SerializedFile, index: number) {
		const key = fileKey + index;
		let fileIcon;
		switch (file.type.toLowerCase()) {
			// Microsoft Excel
			case "application/vnd.ms-excel":
			case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
			case "application/vnd.openxmlformats-officedocument.spreadsheetml.template":
			case "application/vnd.ms-excel.sheet.macroEnabled.12":
			case "application/vnd.ms-excel.template.macroEnabled.12":
			case "application/vnd.ms-excel.addin.macroEnabled.12":
			case "application/vnd.ms-excel.sheet.binary.macroEnabled.12":
				fileIcon = ["far", "file-excel"];
				break;
			// PDF
			case "application/pdf":
				fileIcon = ["far", "file-pdf"];
				break;
			// Microsoft Word
			case "application/msword":
			case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
			case "application/vnd.ms-word.document.macroEnabled.12":
			case "application/vnd.openxmlformats-officedocument.wordprocessingml.template":
			case "application/vnd.ms-word.template.macroEnabled.12":
				fileIcon = ["far", "file-word"];
				break;
			default:
				fileIcon = ["far", "file"];
				break;
		}
		return (
			<a href={file.url} download={file.name} key={file.url}>
				<div
					key={key}
					className={styles(style.fileContainer, showIcons ? null : style.noIcons)}
					title={file.name}
				>
					<div className={style.closeContainer} title={file.name}>
						<span onClick={(e) => removeFile(e, index)}>
							<FontAwesomeIcon icon="times" title={file.name} />
						</span>
					</div>
					{showIcons ? (
						<div className={style.iconContainer} title={file.name}>
							<FontAwesomeIcon icon={fileIcon as IconProp} size={"4x"} />
						</div>
					) : null}
					<p className={style.nameContainer} title={file.name}>
						{file.name}
					</p>
				</div>
			</a>
		);
	}
};

interface FileListProps {
	files: SerializedFile[];
	showIcons: boolean;
	removeFile: (event: MouseEvent<HTMLSpanElement>, index: number) => void;
}
